import { useClientPortalAPI } from "@certn/api";
import {
  ActionList,
  Divider,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import { Fragment, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";

import { useHandleChange } from "../utils";
import { QuickscreenConsentDocumentItem } from "./QuickscreenConsentDocumentItem";

export function QuickscreenConsentsContent({
  caseId,
  acceptedPath,
  onEmptyConsentsChange,
}: {
  caseId: string;
  acceptedPath: string;
  onEmptyConsentsChange?: (isEmpty: boolean) => void;
}) {
  const { t } = useTranslation();
  const handleChange = useHandleChange();

  const {
    data: responseData,
    isLoading,
    error,
  } = useClientPortalAPI(
    caseId
      ? `/api/internal/client-portal/quickscreen-case/${caseId}/agreement-templates/`
      : null
  );

  const agreementData = responseData;
  const consents = agreementData?.consents ?? [];
  // True only once the API has actually responded with zero consents — keeps
  // the parent layout from hiding the acknowledgement during initial load.
  const hasLoadedEmptyConsents =
    !isLoading && !error && !!agreementData && consents.length === 0;

  useLayoutEffect(() => {
    onEmptyConsentsChange?.(hasLoadedEmptyConsents);
  }, [hasLoadedEmptyConsents, onEmptyConsentsChange]);

  useLayoutEffect(() => {
    if (hasLoadedEmptyConsents) {
      handleChange(acceptedPath, true);
    }
  }, [hasLoadedEmptyConsents, acceptedPath, handleChange]);

  if (isLoading) {
    return <Loader height="200px" />;
  }

  if (error) {
    return (
      <SectionMessage type="danger" testID="consents-error">
        {t("Something went wrong loading the consent documents.")}
      </SectionMessage>
    );
  }

  if (hasLoadedEmptyConsents) {
    return null;
  }

  return (
    <ActionList>
      {consents.map((consent) => (
        <Fragment key={consent.id}>
          <QuickscreenConsentDocumentItem consent={consent} caseId={caseId} />
          <Divider />
        </Fragment>
      ))}
    </ActionList>
  );
}
